// src/components/CostingCard.jsx
//
// Project/Site Costing Card — reads financial data for a single project.
//
// ARCHITECTURE (why it's built this way, for future maintainers):
//
// 1. Headline totals (Total Income / Total Expense / Profit) come from the
//    EXISTING project_summary/{company_id}_{project_id} doc — already kept
//    live by the onFinancialTransactionCreate Cloud Function trigger on
//    every financial_transactions write. Per ERP_CONSTITUTION's Cache Rule,
//    this is a CACHE (always replayable from financial_transactions, never
//    a second source of truth) — reading it here is just reusing an
//    existing, already-correct cache. No new Cloud Function logic needed.
//
// 2. Category breakdown (Material vs Equipment vs Labour vs Transport vs
//    Expense sub-types) is computed FRESH, every time this card loads, by
//    querying financial_transactions directly (the actual Source of Truth
//    per the Constitution's SOT map) and grouping client-side. This is the
//    permanent/self-healing part of the design: it is NOT a second cache
//    that needs to be kept in sync with anything. If a future session adds
//    a brand-new cost category (a new expense type, a new module entirely),
//    it appears here automatically the moment that module starts writing
//    financial_transactions with a project_id — nothing to remember to
//    wire into this component.
//
// This deliberately avoids editing the existing Cloud Function trigger —
// changing a production trigger to add per-category counters would be a
// second, fragile cache that could drift from the ledger; querying the
// ledger directly here can never drift, by construction.

import { useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";

const CATEGORY_LABELS = {
  material:      "Material",
  equipment:     "Equipment",
  subcontractor: "Subcontractor",
  labour:        "Labour",
  transport:     "Transport",
  travel:        "Travel / Fuel",
  food:          "Food / Meals",
  misc:          "Miscellaneous",
  expense:       "Other Expense",
};

const CATEGORY_COLORS = {
  material:      "#0ea5e9",
  equipment:     "#8b5cf6",
  subcontractor: "#dc2626",
  labour:        "#f59e0b",
  transport:     "#10b981",
  travel:        "#06b6d4",
  food:          "#f97316",
  misc:          "#64748b",
  expense:       "#94a3b8",
};

export default function CostingCard({ companyId, projectId, contractValue, progressPct }) {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId || !projectId) return;
    loadCosting();
  }, [companyId, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCosting = async () => {
    setLoading(true);
    setError(null);
    try {
      // Headline totals — existing trigger-maintained cache, unchanged.
      const summarySnap = await getDoc(doc(db, "project_summary", `${companyId}_${projectId}`));
      setSummary(summarySnap.exists() ? summarySnap.data() : null);

      // Category breakdown — computed fresh from financial_transactions
      // (SOT) on every load. Only "expense" type entries are grouped here;
      // income is already correctly totalled via project_summary above.
      const txnSnap = await getDocs(query(
        collection(db, "financial_transactions"),
        where("company_id", "==", companyId),
        where("project_id", "==", projectId)
      ));

      const byCategory = {};
      txnSnap.docs.forEach(d => {
        const t = d.data();
        if (t.type !== "expense") return;
        const cat = t.category || "misc";
        byCategory[cat] = (byCategory[cat] || 0) + Number(t.amount || 0);
      });

      const breakdown = Object.entries(byCategory)
        .map(([category, amount]) => ({ category, amount }))
        .sort((a, b) => b.amount - a.amount);

      setCategoryBreakdown(breakdown);
    } catch (err) {
      console.error("CostingCard load error:", err);
      // Most likely cause on first-ever load in a given environment: a
      // missing Firestore composite index for (company_id ==, project_id ==)
      // on financial_transactions. Firestore's own console error includes a
      // direct "create index" link the first time this query runs — click
      // it once per Firebase project (dev and prod are independent, per
      // this project's standing dev/prod-deploy-separately rule), and the
      // query works from then on with no code change needed.
      setError("Could not load cost breakdown. If this is the first time this page has loaded, check the browser console for a Firestore \"create index\" link.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <h2 className="font-semibold text-gray-700 mb-3">💰 Project Costing</h2>
        <p className="text-sm text-gray-400">Loading cost data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <h2 className="font-semibold text-gray-700 mb-3">💰 Project Costing</h2>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  const totalIncome  = Number(summary?.total_income  || 0);
  const totalExpense = Number(summary?.total_expense || 0);
  const profit        = summary
    ? Number(summary.profit ?? (totalIncome - totalExpense))
    : (totalIncome - totalExpense);

  const budget   = Number(contractValue || 0);
  const spendPct = budget > 0 ? Math.round((totalExpense / budget) * 100) : null;

  // Health indicator — flags margin erosion before it's visible from raw
  // totals: cost running ahead of physical progress is the earliest signal
  // a project may be heading over-budget.
  let health = null;
  if (spendPct !== null && typeof progressPct === "number") {
    const diff = spendPct - progressPct;
    if (diff > 15) {
      health = { label: "⚠ Spend outpacing progress", color: "text-red-600 bg-red-50 border-red-200" };
    } else if (diff > 5) {
      health = { label: "⚠ Spend slightly ahead of progress", color: "text-amber-600 bg-amber-50 border-amber-200" };
    } else {
      health = { label: "✓ Spend in line with progress", color: "text-green-600 bg-green-50 border-green-200" };
    }
  }

  const maxCategoryAmount = categoryBreakdown.length > 0 ? categoryBreakdown[0].amount : 0;

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 space-y-4">
      <h2 className="font-semibold text-gray-700">💰 Project Costing</h2>

      {/* HEADLINE NUMBERS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <div className="text-lg font-bold text-green-700">₹{totalIncome.toLocaleString("en-IN")}</div>
          <div className="text-xs text-gray-400">Total Income</div>
        </div>
        <div>
          <div className="text-lg font-bold text-red-600">₹{totalExpense.toLocaleString("en-IN")}</div>
          <div className="text-xs text-gray-400">Total Spent</div>
        </div>
        <div>
          <div className={`text-lg font-bold ${profit >= 0 ? "text-blue-700" : "text-red-600"}`}>
            ₹{profit.toLocaleString("en-IN")}
          </div>
          <div className="text-xs text-gray-400">Profit / Margin</div>
        </div>
        <div>
          <div className="text-lg font-bold text-gray-700">
            {spendPct !== null ? `${spendPct}%` : "—"}
          </div>
          <div className="text-xs text-gray-400">Budget Spent</div>
        </div>
      </div>

      {/* HEALTH INDICATOR */}
      {health && (
        <div className={`text-xs font-semibold px-3 py-2 rounded-lg border ${health.color}`}>
          {health.label} — {progressPct}% work done vs {spendPct}% budget spent
        </div>
      )}

      {/* CATEGORY BREAKDOWN */}
      {categoryBreakdown.length === 0 ? (
        <p className="text-sm text-gray-400">No costs recorded against this project yet.</p>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase">Cost Breakdown by Category</p>
          {categoryBreakdown.map(({ category, amount }) => (
            <div key={category}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">{CATEGORY_LABELS[category] || category}</span>
                <span className="font-semibold text-gray-700">₹{amount.toLocaleString("en-IN")}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full"
                  style={{
                    width: maxCategoryAmount > 0 ? `${(amount / maxCategoryAmount) * 100}%` : "0%",
                    background: CATEGORY_COLORS[category] || "#94a3b8",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
